import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Pipette, History, Check, ChevronDown, X, Pencil, Trash2 } from "lucide-react";

const STORAGE_KEY = "selllocalhomes_recent_colors";

// Get recent colors from localStorage
const getRecentColors = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

// Save recent colors to localStorage
const saveRecentColors = (colors) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(colors.slice(0, 10)));
  } catch {
    // Ignore storage errors
  }
};

// Add color to recent colors
const addRecentColor = (color) => {
  if (!color || !color.startsWith("#")) return getRecentColors();
  const normalizedColor = color.toUpperCase();
  let recent = getRecentColors();
  recent = recent.filter(c => c.toUpperCase() !== normalizedColor);
  recent.unshift(normalizedColor);
  recent = recent.slice(0, 10);
  saveRecentColors(recent);
  return recent;
};

// Convert HSV to RGB
const hsvToRgb = (h, s, v) => {
  let r, g, b;
  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);
  
  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
    default: r = 0; g = 0; b = 0;
  }
  
  return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
};

// Convert RGB to Hex
const rgbToHex = (r, g, b) => {
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('').toUpperCase();
};

// Convert Hex to HSV
const hexToHsv = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return [0, 0, 1];
  
  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  
  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;
  
  if (max !== min) {
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  return [h, s, v];
};

// Spectrum Color Picker Component
const SpectrumPicker = ({ color, onChange }) => {
  const [hsv, setHsv] = useState(() => hexToHsv(color));
  const satValRef = useRef(null);
  const hueRef = useRef(null);
  const isDraggingSatVal = useRef(false);
  const isDraggingHue = useRef(false);

  useEffect(() => {
    setHsv(hexToHsv(color));
  }, [color]);

  const updateColor = useCallback((newHsv) => {
    const [r, g, b] = hsvToRgb(newHsv[0], newHsv[1], newHsv[2]);
    const hex = rgbToHex(r, g, b);
    onChange(hex);
  }, [onChange]);

  const handleSatValMove = useCallback((e) => {
    if (!satValRef.current) return;
    const rect = satValRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0, Math.min(1, (e.clientY - rect.top) / rect.height));
    const newHsv = [hsv[0], x, 1 - y];
    setHsv(newHsv);
    updateColor(newHsv);
  }, [hsv, updateColor]);

  const handleHueMove = useCallback((e) => {
    if (!hueRef.current) return;
    const rect = hueRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const newHsv = [x, hsv[1], hsv[2]];
    setHsv(newHsv);
    updateColor(newHsv);
  }, [hsv, updateColor]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingSatVal.current) handleSatValMove(e);
      if (isDraggingHue.current) handleHueMove(e);
    };
    
    const handleMouseUp = () => {
      isDraggingSatVal.current = false;
      isDraggingHue.current = false;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleSatValMove, handleHueMove]);

  const [hueR, hueG, hueB] = hsvToRgb(hsv[0], 1, 1);
  const hueColor = rgbToHex(hueR, hueG, hueB);

  return (
    <div className="space-y-3">
      {/* Saturation/Value picker */}
      <div
        ref={satValRef}
        className="relative w-full h-40 rounded cursor-crosshair"
        style={{
          background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, ${hueColor})`
        }}
        onMouseDown={(e) => {
          isDraggingSatVal.current = true;
          handleSatValMove(e);
        }}
      >
        {/* Picker indicator */}
        <div
          className="absolute w-4 h-4 border-2 border-white rounded-full shadow-md -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={{
            left: `${hsv[1] * 100}%`,
            top: `${(1 - hsv[2]) * 100}%`,
            backgroundColor: color
          }}
        />
      </div>

      {/* Hue slider */}
      <div
        ref={hueRef}
        className="relative w-full h-4 rounded cursor-pointer"
        style={{
          background: 'linear-gradient(to right, #f00, #ff0, #0f0, #0ff, #00f, #f0f, #f00)'
        }}
        onMouseDown={(e) => {
          isDraggingHue.current = true;
          handleHueMove(e);
        }}
      >
        {/* Hue indicator */}
        <div
          className="absolute w-4 h-6 border-2 border-white rounded shadow-md -translate-x-1/2 -top-1 pointer-events-none"
          style={{
            left: `${hsv[0] * 100}%`,
            backgroundColor: hueColor
          }}
        />
      </div>
    </div>
  );
};

export const ColorPicker = ({ 
  label, 
  value, 
  onChange, 
  description,
  testId 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [recentColors, setRecentColors] = useState(() => getRecentColors());
  const [internalValue, setInternalValue] = useState(value || "#000000");
  const [editMode, setEditMode] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editValue, setEditValue] = useState("");
  const containerRef = useRef(null);
  
  // Sync with external value when it changes
  useEffect(() => {
    if (value && value !== internalValue) {
      setInternalValue(value);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setEditMode(false);
        setEditingIndex(null);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleColorChange = (color) => {
    const normalizedColor = color.toUpperCase();
    setInternalValue(normalizedColor);
    onChange(normalizedColor);
  };

  const handleColorSelect = (color) => {
    handleColorChange(color);
    const updatedRecent = addRecentColor(color);
    setRecentColors(updatedRecent);
  };

  const handleHexInputChange = (e) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    if (/^#[0-9A-Fa-f]{6}$/.test(newValue)) {
      onChange(newValue.toUpperCase());
    }
  };

  const handleHexInputBlur = () => {
    if (/^#[0-9A-Fa-f]{6}$/.test(internalValue)) {
      const normalized = internalValue.toUpperCase();
      setInternalValue(normalized);
      onChange(normalized);
      const updatedRecent = addRecentColor(normalized);
      setRecentColors(updatedRecent);
    }
  };

  // Edit recent color
  const startEditColor = (index) => {
    setEditingIndex(index);
    setEditValue(recentColors[index]);
  };

  const saveEditColor = () => {
    if (editingIndex !== null && /^#[0-9A-Fa-f]{6}$/.test(editValue)) {
      const newRecent = [...recentColors];
      newRecent[editingIndex] = editValue.toUpperCase();
      setRecentColors(newRecent);
      saveRecentColors(newRecent);
      setEditingIndex(null);
      setEditValue("");
    }
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditValue("");
  };

  const deleteRecentColor = (index) => {
    const newRecent = recentColors.filter((_, i) => i !== index);
    setRecentColors(newRecent);
    saveRecentColors(newRecent);
  };

  const clearRecentColors = () => {
    setRecentColors([]);
    saveRecentColors([]);
    setEditMode(false);
  };

  return (
    <div className="space-y-2" ref={containerRef}>
      <Label className="flex items-center gap-2 text-sm font-medium">
        <Pipette size={14} className="text-muted-foreground" />
        {label}
      </Label>
      
      {/* Main Color Display & Toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => { setIsOpen(!isOpen); setEditMode(false); }}
          className="w-14 h-10 rounded border-2 border-border hover:border-primary transition-colors flex items-center justify-center"
          style={{ backgroundColor: internalValue }}
          data-testid={`${testId}-swatch`}
        >
          <ChevronDown 
            size={14} 
            className={`text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)] transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </button>
        
        <Input
          value={internalValue}
          onChange={handleHexInputChange}
          onBlur={handleHexInputBlur}
          placeholder="#000000"
          className="flex-1 font-mono text-sm uppercase"
          data-testid={testId}
          maxLength={7}
        />
      </div>

      {/* Expanded Color Picker Panel */}
      {isOpen && (
        <div className="bg-white border border-border rounded-md p-4 shadow-lg space-y-4 animate-in fade-in-0 zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-foreground">Color Picker</span>
            <button
              type="button"
              onClick={() => { setIsOpen(false); setEditMode(false); }}
              className="p-1 hover:bg-muted rounded-sm transition-colors"
            >
              <X size={16} className="text-muted-foreground" />
            </button>
          </div>

          {/* Spectrum Picker - Infinite combinations */}
          <SpectrumPicker 
            color={internalValue} 
            onChange={handleColorChange}
          />

          {/* Hex Input in panel */}
          <div className="flex gap-2 items-center">
            <div 
              className="w-10 h-10 rounded border border-border shadow-inner flex-shrink-0"
              style={{ backgroundColor: internalValue }}
            />
            <Input
              value={internalValue}
              onChange={handleHexInputChange}
              onBlur={handleHexInputBlur}
              placeholder="#000000"
              className="font-mono text-sm uppercase"
              maxLength={7}
            />
            <Button
              type="button"
              size="sm"
              onClick={() => {
                const updatedRecent = addRecentColor(internalValue);
                setRecentColors(updatedRecent);
              }}
              className="whitespace-nowrap"
            >
              Save Color
            </Button>
          </div>

          {/* Recent Colors Section */}
          {recentColors.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <History size={14} className="text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Recent ({recentColors.length})
                  </span>
                </div>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setEditMode(!editMode)}
                    className={`p-1 rounded-sm transition-colors ${editMode ? 'bg-primary text-white' : 'hover:bg-muted text-muted-foreground'}`}
                    title="Edit recent colors"
                  >
                    <Pencil size={12} />
                  </button>
                  {editMode && (
                    <button
                      type="button"
                      onClick={clearRecentColors}
                      className="p-1 hover:bg-destructive/10 text-destructive rounded-sm transition-colors"
                      title="Clear all"
                    >
                      <Trash2 size={12} />
                    </button>
                  )}
                </div>
              </div>
              
              <div className="flex flex-wrap gap-1.5">
                {recentColors.map((color, index) => (
                  <div key={`recent-${index}`} className="relative group">
                    {editingIndex === index ? (
                      <div className="flex gap-1">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-20 h-8 px-1 text-xs font-mono border rounded"
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveEditColor();
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                        <button
                          type="button"
                          onClick={saveEditColor}
                          className="p-1 bg-primary text-white rounded"
                        >
                          <Check size={12} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => !editMode && handleColorSelect(color)}
                          className={`w-8 h-8 rounded border-2 transition-all relative ${
                            color.toUpperCase() === internalValue.toUpperCase() 
                              ? 'border-primary ring-2 ring-primary/30' 
                              : 'border-border hover:border-primary'
                          } ${editMode ? 'cursor-default' : 'hover:scale-110'}`}
                          style={{ backgroundColor: color }}
                          title={color}
                        >
                          {color.toUpperCase() === internalValue.toUpperCase() && !editMode && (
                            <Check size={14} className="absolute inset-0 m-auto text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]" />
                          )}
                        </button>
                        {editMode && (
                          <div className="absolute -top-1 -right-1 flex gap-0.5">
                            <button
                              type="button"
                              onClick={() => startEditColor(index)}
                              className="w-4 h-4 bg-blue-500 text-white rounded-full flex items-center justify-center"
                            >
                              <Pencil size={8} />
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteRecentColor(index)}
                              className="w-4 h-4 bg-red-500 text-white rounded-full flex items-center justify-center"
                            >
                              <X size={8} />
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Description */}
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
    </div>
  );
};

export default ColorPicker;
